// WpDevConsole — interactive SSH terminal for a WP site, embedded in the
// WP Management page. Mints a single-use token, opens the brokered
// /api/wp/console WebSocket, and wires xterm.js to a direct ssh2 shell on the
// site's server (see backend wpDev.js).
//   keystrokes → BINARY frames · resize → TEXT {type:"resize",cols,rows}
//   shell output ← BINARY frames → term.write
import { useEffect, useRef, useState, useCallback } from "react";
import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import "@xterm/xterm/css/xterm.css";

const API = "https://ticketing.midtc.com/api";
const WS_BASE = API.replace(/^http/, "ws").replace(/\/api$/, "");
const tok = () => localStorage.getItem("midtc_token") || "";

export default function WpDevConsole({ siteId, sshConfigured }) {
  const hostRef = useRef(null);
  const termRef = useRef(null);
  const fitRef  = useRef(null);
  const wsRef   = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | connecting | open | closed | error
  const [detail, setDetail] = useState("");
  const [nonce, setNonce]   = useState(0);      // reconnect trigger

  const connect = useCallback(async () => {
    setStatus("connecting"); setDetail("");
    // token
    let token;
    try {
      const r = await fetch(`${API}/wp/sites/${siteId}/console/token`, { method: "POST", headers: { Authorization: `Bearer ${tok()}` } });
      const d = await r.json();
      if (!r.ok) { setStatus("error"); setDetail(d.error || "Could not start a session"); return; }
      token = d.token;
    } catch { setStatus("error"); setDetail("Could not reach the server"); return; }

    // terminal
    const term = new Terminal({
      cursorBlink: true, fontSize: 13, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      theme: { background: "#0b1120", foreground: "#e2e8f0", cursor: "#60a5fa" }, scrollback: 5000,
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(hostRef.current);
    try { fit.fit(); } catch {}
    termRef.current = term; fitRef.current = fit;

    const ws = new WebSocket(`${WS_BASE}/api/wp/console?token=${encodeURIComponent(token)}`);
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;

    ws.onopen = () => {
      setStatus("open");
      const send = () => { try { ws.send(JSON.stringify({ type: "resize", cols: term.cols, rows: term.rows })); } catch {} };
      send();
      term.onData(d => { try { ws.readyState === 1 && ws.send(new TextEncoder().encode(d)); } catch {} });
      term.onResize(() => send());
    };
    ws.onmessage = (evt) => {
      if (typeof evt.data === "string") term.write(evt.data);
      else term.write(new Uint8Array(evt.data));
    };
    ws.onclose = () => { setStatus(s => s === "error" ? s : "closed"); };
    ws.onerror = () => { setStatus("error"); setDetail("Connection error"); };
  }, [siteId]);

  useEffect(() => {
    if (nonce === 0) return; // only connect when the user asks
    connect();
    return () => {
      try { wsRef.current?.close(); } catch {}
      try { termRef.current?.dispose(); } catch {}
      wsRef.current = null; termRef.current = null;
    };
  }, [nonce, connect]);

  // Refit on container resize
  useEffect(() => {
    const onResize = () => { try { fitRef.current?.fit(); } catch {} };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  if (!sshConfigured) {
    return (
      <div style={{ padding: 30, textAlign: "center", color: "#64748b", fontSize: 13, lineHeight: 1.7 }}>
        SSH isn't configured for this site yet.<br />
        Add the host, user and password/key under the <b>Connection</b> tab, then come back here.
      </div>
    );
  }

  const dot = { open: "#34d399", connecting: "#fbbf24", closed: "#64748b", error: "#f87171", idle: "#64748b" }[status];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "#94a3b8" }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: dot }} />
          {status === "open" ? "Connected" : status === "connecting" ? "Connecting…" : status === "closed" ? "Session ended" : status === "error" ? "Error" : "Not connected"}
        </span>
        {detail && <span style={{ fontSize: 12, color: "#f87171" }}>{detail}</span>}
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          {(status === "idle" || status === "closed" || status === "error") && (
            <button onClick={() => setNonce(n => n + 1)}
              style={{ background: "linear-gradient(135deg,#10b981,#0891b2)", color: "#fff", border: "none", borderRadius: 7, padding: "6px 16px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              {status === "closed" || status === "error" ? "↻ Reconnect" : "▶ Connect SSH"}
            </button>
          )}
          {status === "open" && (
            <button onClick={() => { try { wsRef.current?.close(); } catch {} }}
              style={{ background: "rgba(239,68,68,0.1)", color: "#f87171", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 7, padding: "6px 16px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              Disconnect
            </button>
          )}
        </div>
      </div>
      <div ref={hostRef} style={{ height: 460, background: "#0b1120", borderRadius: 10, border: "1px solid var(--t-border-soft)", padding: 8, overflow: "hidden", display: nonce === 0 ? "none" : "block" }} />
      {nonce === 0 && (
        <div style={{ height: 200, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 12, color: "#475569", background: "#0b1120", borderRadius: 10, border: "1px solid var(--t-border-soft)" }}>
          <div style={{ fontSize: 34 }}>⌨️</div>
          <div style={{ fontSize: 13 }}>Click <b style={{ color: "#94a3b8" }}>Connect SSH</b> to open a shell — run git, wp-cli, composer, npm, whatever you need.</div>
        </div>
      )}
    </div>
  );
}
